import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Key, Trash2, X, ShieldCheck, ShieldAlert,
         GraduationCap, Unlock, ChevronDown, ChevronRight, RefreshCw, LogOut } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_LABELS = {
  admin: "Администратор",
  senior_manager: "Старший менеджер",
  manager: "Менеджер",
  accountant: "Бухгалтер",
  trainee: "Стажёр",
};

const ROLE_COLORS = {
  admin: "#7c3aed",
  senior_manager: "#0ea5e9",
  manager: "#16a34a",
  accountant: "#f59e0b",
  trainee: "#8b5cf6",
};

function generatePassword(length = 12) {
  // Excludes confusing chars (0/O, 1/l/I) so it's safe to dictate over phone.
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
  let out = "";
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  for (let i = 0; i < length; i++) out += chars[arr[i] % chars.length];
  return out;
}

function StaffFormModal({ onClose, onSaved, editing, currentUserEmail }) {
  const isNew = !editing;
  const [email, setEmail] = useState(editing?.email || "");
  const [name, setName] = useState(editing?.name || "");
  const [phone, setPhone] = useState(editing?.phone || "");
  const [role, setRole] = useState(editing?.role || "manager");
  const [password, setPassword] = useState(isNew ? generatePassword() : "");
  const [showPassword, setShowPassword] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isSelf = editing?.email === currentUserEmail;

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/staff`, { email, name, phone, role, password }, { withCredentials: true });
        onSaved({ email, password }); // surface password to parent for the banner
      } else {
        const body = { name, phone };
        if (!isSelf) body.role = role;
        await axios.patch(`${API}/admin/staff/${editing.id}`, body, { withCredentials: true });
        onSaved(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div data-testid="staff-form-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>
            {isNew ? "Новый сотрудник" : `Редактировать: ${editing.name}`}
          </h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        {error && <div data-testid="staff-form-error" style={errorBox}>{error}</div>}

        <label style={labelStyle}>
          Email
          <input
            data-testid="staff-email-input"
            type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!isNew}
            placeholder="manager@angiolini.kz"
            style={{ ...inputStyle, background: isNew ? "white" : "#f3f4f6", color: isNew ? "#111" : "#6b7280" }}
          />
        </label>

        <label style={labelStyle}>
          Имя
          <input
            data-testid="staff-name-input"
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Менеджер онлайн-отдела"
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Телефон (опционально)
          <input
            data-testid="staff-phone-input"
            type="tel" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+7 701 000 00 00"
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Роль {isSelf && <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>(нельзя изменить свою)</span>}
          <select
            data-testid="staff-role-select"
            value={role} onChange={(e) => setRole(e.target.value)}
            disabled={isSelf}
            style={{ ...inputStyle, cursor: isSelf ? "not-allowed" : "pointer" }}
          >
            <option value="trainee">Стажёр (Академия)</option>
            <option value="manager">Менеджер</option>
            <option value="accountant">Бухгалтер</option>
            <option value="senior_manager">Старший менеджер</option>
            <option value="admin">Администратор</option>
          </select>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.5 }}>
            <strong>Стажёр</strong> — попадает в Angiolini Academy, изучает уроки и тесты, не видит реальных клиентов и товаров. После экзамена вы вручную меняете роль на «Менеджер».<br />
            <strong>Менеджер</strong> — видит CRM и своих клиентов, не видит финансовых отчётов и других менеджеров.<br />
            <strong>Бухгалтер</strong> — Поступления, Склад, Финотчёты. НЕ видит CRM клиентов и других менеджеров.<br />
            <strong>Старший менеджер</strong> — всё что менеджер + Поступления/Склад/Финансы + полная воронка, может управлять другими менеджерами.<br />
            <strong>Администратор</strong> — полный доступ + товары, новости, сотрудники, безопасность.
          </div>
        </label>

        {isNew && (
          <label style={labelStyle}>
            Пароль (минимум 8 символов)
            <div style={{ display: "flex", gap: 8 }}>
              <input
                data-testid="staff-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={secondaryBtn}>
                {showPassword ? "Скрыть" : "Показать"}
              </button>
              <button type="button" onClick={() => setPassword(generatePassword())} style={secondaryBtn}>
                ⟳ Новый
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 6 }}>
              ⚠ Сохраните пароль — после закрытия окна вы его не увидите.
              Передайте сотруднику через защищённый канал (Telegram, WhatsApp в личке).
            </div>
          </label>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={secondaryBtn}>Отмена</button>
          <button data-testid="staff-form-submit" onClick={submit} disabled={busy} style={primaryBtn}>
            {busy ? "Сохранение…" : (isNew ? "Создать" : "Сохранить")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ staff, onClose, onSaved }) {
  const [password, setPassword] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/staff/${staff.id}/reset-password`, { password }, { withCredentials: true });
      onSaved(password);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div data-testid="reset-password-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>
            Сбросить пароль: {staff.name}
          </h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        <label style={labelStyle}>
          Новый пароль
          <div style={{ display: "flex", gap: 8 }}>
            <input
              data-testid="reset-password-input"
              type="text" value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }}
            />
            <button type="button" onClick={() => setPassword(generatePassword())} style={secondaryBtn}>⟳</button>
          </div>
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
            ⚠ После сохранения старый пароль перестанет работать, а сотрудника
            сразу выбросит из приложения на всех устройствах — войти он сможет
            только с новым паролем.
          </div>
        </label>

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={secondaryBtn}>Отмена</button>
          <button data-testid="reset-password-submit" onClick={submit} disabled={busy} style={primaryBtn}>
            {busy ? "Сохранение…" : "Сбросить пароль"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminStaff({ currentUserEmail }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [logoutBusy, setLogoutBusy] = useState(null);
  const [notice, setNotice] = useState("");

  const handleForceLogout = async (s) => {
    if (!window.confirm(
      `Выбросить ${s.name} из приложения? Все его сессии закроются, ` +
      `он увидит экран входа и зайдёт только по действующему паролю.`)) return;
    setLogoutBusy(s.id);
    try {
      await axios.post(`${API}/admin/staff/${s.id}/force-logout`, {},
        { withCredentials: true });
      setNotice(`${s.name}: все сессии завершены — сотрудника выбросило из приложения`);
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Не удалось завершить сессии");
    } finally { setLogoutBusy(null); }
  };
  const [recentPassword, setRecentPassword] = useState(null); // {email, password}
  const [accessDenied, setAccessDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/staff`, { withCredentials: true });
      setStaff(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/admin/staff/${s.id}`, { is_active: !s.is_active }, { withCredentials: true });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Ошибка");
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Удалить аккаунт ${s.email}? Это действие необратимо.`)) return;
    try {
      await axios.delete(`${API}/admin/staff/${s.id}`, { withCredentials: true });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Ошибка");
    }
  };

  if (accessDenied) {
    return (
      <div data-testid="staff-access-denied" style={{ padding: 40, textAlign: "center", color: "#64748b", fontFamily: "Manrope" }}>
        <ShieldAlert size={48} style={{ margin: "0 auto 12px", display: "block", color: "#dc2626" }} />
        <div style={{ fontSize: 16, fontWeight: 600, color: "#111", marginBottom: 6 }}>Доступ ограничен</div>
        <div>Управление сотрудниками доступно только Администратору.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0 }}>Сотрудники</h2>
          <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 4 }}>
            Управление аккаунтами администраторов и менеджеров CRM
          </div>
        </div>
        <button
          data-testid="add-staff-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: 6, padding: "10px 20px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={16} />Добавить сотрудника
        </button>
      </div>

      {recentPassword && (
        <div data-testid="recent-password-banner" style={{
          background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8,
          padding: "12px 14px", marginBottom: 16, fontFamily: "Manrope", fontSize: 13, color: "#78350f",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>✅ Пароль для {recentPassword.email}:</div>
          <div style={{ fontFamily: "monospace", fontSize: 15, background: "white", padding: "6px 10px", borderRadius: 4, marginBottom: 4, userSelect: "all" }}>
            {recentPassword.password}
          </div>
          <div style={{ fontSize: 11, color: "#92400e" }}>
            ⚠ Скопируйте сейчас, передайте сотруднику через Telegram/WhatsApp и закройте баннер.
            После закрытия пароль не восстановить — придётся сбрасывать.
            Сотрудник уже разлогинен на всех устройствах и войдёт только с этим паролем.
          </div>
          <button onClick={() => setRecentPassword(null)} style={{ marginTop: 8, background: "transparent", border: "1px solid #92400e", color: "#92400e", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
            Я скопировал, закрыть
          </button>
        </div>
      )}

      {notice && (
        <div data-testid="staff-notice" style={{
          background: "#EFF6FF", border: "1px solid #93C5FD", borderRadius: 8,
          padding: "10px 14px", marginBottom: 16, fontFamily: "Manrope",
          fontSize: 13, color: "#1E40AF", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 10,
        }}>
          <span>{notice}</span>
          <button onClick={() => setNotice("")}
            style={{ background: "transparent", border: "none", cursor: "pointer",
                      color: "#1E40AF" }}>
            <X size={14} />
          </button>
        </div>
      )}

      <PromotionRequests onPromoted={load} />

      <AcademyReport />

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#888", fontFamily: "Manrope" }}>Загрузка…</div>
      ) : (
        <div style={{ background: "white", borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: 13 }}>
            <thead style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <tr>
                <th style={th}>Имя</th>
                <th style={th}>Email</th>
                <th style={th}>Роль</th>
                <th style={th}>Телефон</th>
                <th style={th}>2FA</th>
                <th style={th}>Статус</th>
                <th style={{ ...th, textAlign: "right" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={td}>
                    {s.name}
                    {s.email === currentUserEmail && <span style={{ marginLeft: 8, fontSize: 11, background: "#dbeafe", color: "#1e40af", padding: "1px 8px", borderRadius: 4 }}>это вы</span>}
                  </td>
                  <td style={{ ...td, color: "#64748b" }}>{s.email}</td>
                  <td style={td}>
                    <span style={{ background: ROLE_COLORS[s.role], color: "white", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                      {ROLE_LABELS[s.role]}
                    </span>
                  </td>
                  <td style={{ ...td, color: "#64748b" }}>{s.phone || "—"}</td>
                  <td style={td}>{s.totp_enabled ? <ShieldCheck size={16} color="#16a34a" /> : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td style={td}>
                    <button
                      data-testid={`toggle-active-${s.id}`}
                      onClick={() => toggleActive(s)}
                      disabled={s.email === currentUserEmail}
                      style={{
                        background: s.is_active ? "#dcfce7" : "#fee2e2",
                        color: s.is_active ? "#166534" : "#991b1b",
                        border: "none", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                        cursor: s.email === currentUserEmail ? "not-allowed" : "pointer",
                        opacity: s.email === currentUserEmail ? 0.5 : 1,
                      }}
                    >
                      {s.is_active ? "Активен" : "Отключён"}
                    </button>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button data-testid={`edit-staff-${s.id}`} onClick={() => { setEditing(s); setShowForm(true); }} style={actionBtn} title="Редактировать">
                      <Edit2 size={14} />
                    </button>
                    <button data-testid={`reset-pwd-${s.id}`} onClick={() => setResetting(s)} style={actionBtn} title="Сбросить пароль">
                      <Key size={14} />
                    </button>
                    <button
                      data-testid={`force-logout-${s.id}`}
                      onClick={() => handleForceLogout(s)}
                      disabled={s.email === currentUserEmail || logoutBusy === s.id}
                      style={{ ...actionBtn,
                                color: s.email === currentUserEmail ? "#cbd5e1" : "#b45309",
                                cursor: s.email === currentUserEmail ? "not-allowed" : "pointer" }}
                      title={s.email === currentUserEmail
                        ? "Нельзя разлогинить себя"
                        : "Выбросить из приложения (завершить все сессии)"}
                    >
                      <LogOut size={14} />
                    </button>
                    <button
                      data-testid={`delete-staff-${s.id}`}
                      onClick={() => handleDelete(s)}
                      disabled={s.email === currentUserEmail}
                      style={{ ...actionBtn, color: s.email === currentUserEmail ? "#cbd5e1" : "#dc2626", cursor: s.email === currentUserEmail ? "not-allowed" : "pointer" }}
                      title={s.email === currentUserEmail ? "Нельзя удалить себя" : "Удалить"}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {staff.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope", fontSize: 13 }}>
              Сотрудников пока нет
            </div>
          )}
        </div>
      )}

      {showForm && (
        <StaffFormModal
          editing={editing}
          currentUserEmail={currentUserEmail}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={(created) => {
            // `created` is { email, password } when a new staff is created, else null.
            if (created) setRecentPassword(created);
            setShowForm(false); setEditing(null); load();
          }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          staff={resetting}
          onClose={() => setResetting(null)}
          onSaved={(pwd) => {
            setRecentPassword({ email: resetting.email, password: pwd });
            setResetting(null);
          }}
        />
      )}
    </div>
  );
}

// ---------- styles ----------
const th = { textAlign: "left", padding: "10px 14px", color: "#374151", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "12px 14px", color: "#111", verticalAlign: "middle" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 };
const modalStyle = { background: "white", borderRadius: 10, padding: 24, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto", fontFamily: "Manrope" };
const labelStyle = { display: "block", marginBottom: 14, fontSize: 12, color: "#374151", fontWeight: 600 };
const inputStyle = { width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontFamily: "Manrope", fontSize: 13, marginTop: 6, outline: "none", boxSizing: "border-box" };
const primaryBtn = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: 13 };
const secondaryBtn = { background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 600, fontSize: 13 };
const iconBtn = { background: "transparent", border: "none", cursor: "pointer", color: "#64748b", padding: 4 };
const actionBtn = { background: "transparent", border: "none", padding: 6, cursor: "pointer", color: "#374151", marginLeft: 4 };
const errorBox = { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 };


/**
 * Card block above the staff table — surfaces trainees who finished the
 * Academy final exam and are waiting to be promoted. Admin/senior can
 * approve (role trainee → manager) or reject.
 */
function PromotionRequests({ onPromoted }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    axios.get(`${API}/academy/final-exam/admin/promotion-requests`,
              { withCredentials: true })
      .then(r => setRequests(r.data?.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const act = async (tid, action) => {
    setBusy(prev => ({ ...prev, [tid]: true }));
    try {
      await axios.post(
        `${API}/academy/final-exam/admin/promotion-requests/${tid}/${action}`,
        {}, { withCredentials: true });
      await load();
      onPromoted?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    } finally {
      setBusy(prev => ({ ...prev, [tid]: false }));
    }
  };

  if (loading) return null;
  if (!requests.length) return null;

  return (
    <div data-testid="promotion-requests" style={{
      background: "linear-gradient(135deg,#0A0A0A,#1a1a1a)",
      border: "1px solid #C5A059", borderRadius: 12, padding: 20,
      marginBottom: 20, color: "#e5e7eb",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <ShieldCheck size={20} color="#D4B878" />
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#D4B878" }}>
          Кандидаты на перевод в менеджеры ({requests.length})
        </div>
      </div>
      {requests.map(r => (
        <div key={r.trainee_id} data-testid={`promo-request-${r.trainee_id}`}
          style={{ background: "rgba(197,160,89,0.06)",
                    border: "1px solid rgba(197,160,89,0.2)",
                    borderRadius: 8, padding: 14, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                         alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#e5e7eb" }}>
                🎓 {r.name || "—"} <span style={{ color: "#94a3b8", fontWeight: 400 }}>{r.phone}</span>
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                Общий результат: <b style={{ color: "#22c55e" }}>{r.aggregate_score}%</b>
                {" · "}XP: {r.xp} · Серия: {r.streak_days} дн.
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6,
                             display: "grid",
                             gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))",
                             gap: 4 }}>
                <div>Продукт: {r.part_scores?.product ?? "—"}%</div>
                <div>Продажи: {r.part_scores?.sales ?? "—"}%</div>
                <div>CRM: {r.part_scores?.crm ?? "—"}%</div>
                <div>Возражения: {r.part_scores?.objections ?? "—"}%</div>
                <div>Практика: {r.part_scores?.full_sale ?? "—"}%</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid={`promo-approve-${r.trainee_id}`}
                onClick={() => act(r.trainee_id, "approve")}
                disabled={busy[r.trainee_id]}
                style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                          color: "#0A0A0A", border: "none", borderRadius: 6,
                          padding: "8px 14px", fontWeight: 700, fontSize: 12,
                          cursor: "pointer" }}>
                Перевести в менеджеры
              </button>
              <button data-testid={`promo-reject-${r.trainee_id}`}
                onClick={() => act(r.trainee_id, "reject")}
                disabled={busy[r.trainee_id]}
                style={{ background: "transparent", color: "#a1a1aa",
                          border: "1px solid #4b5563", borderRadius: 6,
                          padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>
                Отклонить
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}



/**
 * «Обучение в Академии» — отчёт по сотрудникам: прогресс уроков, задания
 * песочницы, попытки финального экзамена + снятие 24-часовой блокировки.
 */
function AcademyReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/final-exam/admin/report`,
        { withCredentials: true });
      setRows(r.data?.employees || []);
    } catch (e) {
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetCooldown = async (row) => {
    if (!window.confirm(`Снять блокировку экзамена для ${row.name}? ` +
        `Незавершённая попытка будет аннулирована, сотрудник сможет сдавать сразу.`)) return;
    setBusy(p => ({ ...p, [row.trainee_id]: true }));
    try {
      await axios.post(
        `${API}/academy/final-exam/admin/${row.trainee_id}/reset-cooldown`,
        {}, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Не удалось снять блокировку");
    } finally {
      setBusy(p => ({ ...p, [row.trainee_id]: false }));
    }
  };

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <div data-testid="academy-report" style={{
      background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
      padding: 18, marginBottom: 20, fontFamily: "Manrope",
    }}>
      <div style={{ display: "flex", alignItems: "center",
                     justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <GraduationCap size={20} color="#C5A059" />
          <div>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111" }}>
              Обучение в Академии
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Прогресс сотрудников, результаты экзамена и снятие блокировки
            </div>
          </div>
        </div>
        <button data-testid="academy-report-refresh" onClick={load}
          style={{ ...secondaryBtn, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={13} /> Обновить
        </button>
      </div>

      {rows.map(r => {
        const isOpen = !!open[r.trainee_id];
        return (
          <div key={r.trainee_id} data-testid={`academy-row-${r.trainee_id}`}
            style={{ borderTop: "1px solid #f1f5f9", padding: "12px 0" }}>
            <div style={{ display: "flex", alignItems: "center",
                           justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <button data-testid={`academy-toggle-${r.trainee_id}`}
                onClick={() => setOpen(p => ({ ...p, [r.trainee_id]: !isOpen }))}
                style={{ background: "transparent", border: "none", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 8, padding: 0,
                          textAlign: "left" }}>
                {isOpen ? <ChevronDown size={15} color="#64748b" />
                        : <ChevronRight size={15} color="#64748b" />}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
                    {r.name}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600,
                                    color: ROLE_COLORS[r.role] || "#64748b" }}>
                      {ROLE_LABELS[r.role] || r.role}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    Уроки: {r.lessons_completed}/{r.lessons_total} ({r.progress_pct}%)
                    {" · "}Задания: {r.sandbox_tasks_passed}/4
                    {" · "}XP: {r.xp}
                    {r.best_score !== null && r.best_score !== undefined && (
                      <> {" · "}Экзамен: <b style={{ color: r.exam_passed ? "#16a34a" : "#f97316" }}>
                        {r.best_score}%
                      </b></>
                    )}
                  </div>
                </div>
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {r.cooldown_active && (
                  <span data-testid={`academy-cooldown-${r.trainee_id}`}
                    style={{ background: "#fef3c7", border: "1px solid #fcd34d",
                              color: "#92400e", borderRadius: 20, padding: "4px 10px",
                              fontSize: 11, fontWeight: 700 }}>
                    Блок до {new Date(r.next_allowed_at).toLocaleString("ru-RU",
                      { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                {r.in_progress_attempt_id && (
                  <span style={{ background: "#dbeafe", border: "1px solid #93c5fd",
                                  color: "#1e40af", borderRadius: 20, padding: "4px 10px",
                                  fontSize: 11, fontWeight: 700 }}>
                    Экзамен идёт
                  </span>
                )}
                {(r.cooldown_active || r.in_progress_attempt_id) && (
                  <button data-testid={`academy-reset-${r.trainee_id}`}
                    onClick={() => resetCooldown(r)} disabled={busy[r.trainee_id]}
                    style={{ ...primaryBtn, display: "inline-flex", alignItems: "center",
                              gap: 6, padding: "7px 14px", fontSize: 12 }}>
                    <Unlock size={13} />
                    {busy[r.trainee_id] ? "Снимаем…" : "Снять блок экзамена"}
                  </button>
                )}
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: 10, paddingLeft: 23 }}>
                {r.exam_attempts.length === 0 && (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    Попыток экзамена ещё не было
                  </div>
                )}
                {r.exam_attempts.map(a => (
                  <div key={a.id} style={{ background: "#f9fafb", borderRadius: 8,
                                             padding: "10px 12px", marginBottom: 8,
                                             fontSize: 12, color: "#334155" }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      {new Date(a.started_at).toLocaleString("ru-RU")}
                      {" · "}
                      <span style={{ color: a.status === "passed" ? "#16a34a"
                                            : a.status === "failed" ? "#dc2626" : "#64748b" }}>
                        {({ passed: "сдан", failed: "не сдан",
                            in_progress: "идёт", cancelled: "аннулирована" })[a.status] || a.status}
                      </span>
                      {a.aggregate_score !== null && a.aggregate_score !== undefined
                        && ` · итог ${a.aggregate_score}%`}
                    </div>
                    <div style={{ display: "grid",
                                   gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))",
                                   gap: 4, color: "#64748b" }}>
                      <div>Продукт: {a.part_scores?.product ?? "—"}</div>
                      <div>Продажи: {a.part_scores?.sales ?? "—"}</div>
                      <div>CRM: {a.part_scores?.crm ?? "—"}</div>
                      <div>Возражения: {a.part_scores?.objections ?? "—"}</div>
                      <div>Практика: {a.part_scores?.full_sale ?? "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
